import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	forwardRef,
	HostBinding,
	inject,
	Input,
	OnDestroy,
	OnInit,
	TemplateRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { distinctUntilChanged, takeUntil, tap } from "rxjs/operators";

import { clean } from "@intocare/util";

import { BladeOption, BladePath } from "../interfaces";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => BladeInputComponent),
	multi: true,
};

@Component({
	selector: "ic-blade-input",
	templateUrl: "./blade.component.html",
	styleUrls: ["./blade.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class BladeInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
	private readonly eRef = inject(ElementRef);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly formBuilder = inject(UntypedFormBuilder);

	private destroy$ = new Subject<void>();

	protected innerForm: UntypedFormGroup = this.formBuilder.group({
		value: [""],
		child: [""],
	});

	@Input() options: BladeOption[];
	@Input() parent?: HTMLElement = this.eRef.nativeElement.parentElement;
	@Input() @HostBinding("class.error") error = false;

	@HostBinding("class.disabled") disabled = false;
	@ContentChild("titleTmpl", { static: false }) titleTemplate?: TemplateRef<any>;
	@ContentChild("optionTmpl", { static: false }) optionTemplate?: TemplateRef<any>;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	get selectedOption(): BladeOption {
		const innerValue = this.innerForm.getRawValue().value;

		if (!innerValue || !this.options) {
			return;
		}

		return this.options.find((option) => option.value === innerValue);
	}

	get children(): BladeOption[] {
		if (!this.selectedOption || !this.selectedOption.children) {
			return [];
		}

		return this.selectedOption.children;
	}

	isSelected(option: BladeOption) {
		const innerValue = this.innerForm.getRawValue().value;

		return innerValue === option.value;
	}

	selectOption(option: BladeOption) {
		if (this.disabled) {
			return;
		}

		const { value } = option;

		this.innerForm.setValue({ value, child: null });

		this.cdRef.detectChanges();

		// Scroll the options at the right into view
		this.parent.scrollBy({ left: this.parent.scrollWidth, behavior: "smooth" });

		this.onTouch();
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	writeValue(path: BladePath) {
		if (!path) {
			this.innerForm.reset();

			return;
		}

		this.innerForm.patchValue(path);
		this.cdRef.markForCheck();
	}

	ngOnInit() {
		this.innerForm.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((value) => this.onModelChange(clean(value))),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
